import React, { useState, useEffect, useRef } from 'react';
import { getOptimizedImageUrl, ImageOptimizationOptions } from '../../utils/imageOptimization';

interface OptimizedImageProps {
  src: string | undefined;
  alt: string;
  className?: string;
  width?: number;
  height?: number;
  optimization?: ImageOptimizationOptions;
  loading?: 'lazy' | 'eager';
  placeholder?: 'skeleton' | 'blur';
  onLoad?: () => void;
  onError?: () => void;
  priority?: boolean;
  /** CSS object-position (e.g. "center 30%") for cover crop focal point */
  objectPosition?: string;
}

/**
 * OptimizedImage component for web platform
 * - Uses Vercel Image Optimization API
 * - Supports lazy loading with Intersection Observer
 * - Progressive loading with blur-up effect
 * - Better placeholder states (no grey rectangles)
 */
export const OptimizedImage: React.FC<OptimizedImageProps> = ({
  src,
  alt,
  className = '',
  width,
  height,
  optimization,
  loading = 'lazy',
  placeholder = 'skeleton',
  onLoad,
  onError,
  priority = false,
  objectPosition,
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [isInView, setIsInView] = useState(!loading || loading === 'eager' || priority);
  const containerRef = useRef<HTMLDivElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);

  // Set up Intersection Observer for lazy loading
  useEffect(() => {
    // If eager loading or priority, always show image
    if (loading === 'eager' || priority) {
      setIsInView(true);
      return;
    }

    // For lazy loading, check if element is already in viewport first
    if (loading === 'lazy' && typeof window !== 'undefined' && containerRef.current) {
      // Quick check: if element is already visible, load immediately
      const rect = containerRef.current.getBoundingClientRect();
      const isVisible = rect.top < window.innerHeight + 50 && rect.bottom > -50;
      
      if (isVisible) {
        setIsInView(true);
        return;
      }

      // Otherwise, set up Intersection Observer
      if ('IntersectionObserver' in window) {
        observerRef.current = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) {
                setIsInView(true);
                observerRef.current?.disconnect();
              }
            });
          },
          {
            rootMargin: '50px', // Start loading 50px before image enters viewport
          }
        );

        observerRef.current.observe(containerRef.current);

        return () => {
          if (observerRef.current) {
            observerRef.current.disconnect();
          }
        };
      } else {
        // Fallback for browsers without IntersectionObserver
        setIsInView(true);
      }
    }
  }, [loading, priority]);

  // Calculate optimized source early
  const optimizedSrc = src ? getOptimizedImageUrl(src, optimization) : '';

  const handleLoad = () => {
    setIsLoading(false);
    onLoad?.();
  };

  const handleError = (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
    console.error('Image load error:', { src, optimizedSrc, originalSrc: src }, e);
    setIsLoading(false);
    setHasError(true);
    onError?.();
  };

  if (!src) {
    return (
      <div
        className={`bg-gray-100 flex items-center justify-center ${className}`}
        style={{ width, height }}
      >
        <svg
          className="w-12 h-12 text-gray-400"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
          />
        </svg>
      </div>
    );
  }

  const renderPlaceholder = () => {
    if (hasError) {
      return (
        <div
          className={`bg-gray-100 flex items-center justify-center ${className}`}
          style={{ width, height }}
        >
          <svg
            className="w-12 h-12 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
            />
          </svg>
        </div>
      );
    }

    if (placeholder === 'blur') {
      // Blur-up placeholder - show low quality version first
      const lowQualitySrc = getOptimizedImageUrl(src, { ...optimization, quality: 20, width: optimization?.width ? Math.floor(optimization.width / 10) : 40 });
      return (
        <img
          src={lowQualitySrc}
          alt=""
          className={`absolute inset-0 w-full h-full object-cover blur-sm ${isLoading ? 'opacity-100' : 'opacity-0 transition-opacity duration-300'}`}
          style={{ width, height }}
          aria-hidden="true"
        />
      );
    }

    // Default: skeleton shimmer
    return (
      <div
        className={`absolute inset-0 bg-gradient-to-r from-gray-100 via-gray-200 to-gray-100 animate-pulse ${isLoading ? 'opacity-100' : 'opacity-0 transition-opacity duration-300'}`}
        style={{ width, height }}
        aria-hidden="true"
      />
    );
  };

  return (
    <div ref={containerRef} className={`relative overflow-hidden ${className}`} style={{ width, height }}>
      {isLoading && renderPlaceholder()}
      {isInView && (
        <img
          ref={imgRef}
          src={optimizedSrc}
          alt={alt}
          width={width}
          height={height}
          loading={loading}
          decoding="async"
          className={`w-full h-full object-cover ${isLoading ? 'opacity-0' : 'opacity-100 transition-opacity duration-300'}`}
          style={objectPosition ? { objectPosition } : undefined}
          onLoad={handleLoad}
          onError={(e) => handleError(e)}
        />
      )}
    </div>
  );
};
